import {
  Building2,
  CalendarDays,
  Gift,
  KeyRound,
  Library,
  ScrollText,
  Siren,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { ReactElement } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type BackofficeElement, can } from "@/lib/labels";
import { cn } from "@/lib/utils";

interface NavigationEntry {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  /**
   * The elements this screen serves, one of which it needs (D57). The server decides; this
   * only avoids showing a link whose page would answer 403. Absent means "every
   * operator", which is what the read-only screens are.
   */
  readonly elements?: readonly BackofficeElement[];
  /** The grants page: the superadmin's alone, and no element. */
  readonly superadmin?: true;
}

interface NavigationGroup {
  /** Empty for the daily work at the top, which needs no heading. */
  readonly label: string;
  readonly entries: readonly NavigationEntry[];
}

/**
 * The daily work first, the administration below it (IT lead, 19.10.2026): Mediavertrieb and
 * Redaktion see three entries, and with `campaign_support` the second group is gone entirely.
 * The help moved to the header, one link per page.
 */
export const NAVIGATION: readonly NavigationGroup[] = [
  {
    label: "",
    entries: [
      { to: "/", label: "Aktionen", icon: CalendarDays },
      { to: "/sponsoren", label: "Sponsoren", icon: Building2 },
      { to: "/gewinne", label: "Gewinne", icon: Gift, elements: ["prizes"] },
    ],
  },
  {
    label: "Verwaltung",
    entries: [
      { to: "/teilnehmer", label: "Teilnehmer", icon: Users, elements: ["participants"] },
      { to: "/protokoll", label: "Protokoll", icon: ScrollText, elements: ["audit"] },
      {
        to: "/stammdaten",
        label: "Stammdaten",
        icon: Library,
        elements: ["brands", "consent_templates"],
      },
      { to: "/not-aus", label: "Not-Aus", icon: Siren, elements: ["emergency"] },
      { to: "/berechtigungen", label: "Berechtigungen", icon: KeyRound, superadmin: true },
    ],
  },
];

/**
 * Whether to offer this entry. Hiding is a courtesy, not the guard: the route stays
 * reachable by typing it and answers with its own "no permission" screen, and the
 * endpoints behind it check the role on every request.
 */
function isVisible(
  entry: NavigationEntry,
  granted: readonly string[],
  superadmin: boolean,
): boolean {
  if (entry.superadmin === true) {
    return superadmin;
  }
  return entry.elements === undefined || can(granted, ...entry.elements);
}

/**
 * The entry a path belongs to: an action's pages belong to "Aktionen", a sponsor's to
 * "Sponsoren". `null` for a screen outside the navigation (the help), so nothing is marked.
 */
export function sectionOf(pathname: string): string | null {
  if (pathname === "/" || pathname.startsWith("/aktionen/")) {
    return "/";
  }
  const match = NAVIGATION.flatMap((group) => group.entries)
    .filter((entry) => entry.to !== "/")
    .find((entry) => pathname === entry.to || pathname.startsWith(`${entry.to}/`));
  return match?.to ?? null;
}

export function SideNav({
  granted,
  superadmin,
}: {
  readonly granted: readonly string[];
  readonly superadmin: boolean;
}): ReactElement {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const current = sectionOf(pathname);
  const groups = NAVIGATION.map((group) => ({
    ...group,
    entries: group.entries.filter((entry) => isVisible(entry, granted, superadmin)),
  })).filter((group) => group.entries.length > 0);

  return (
    <>
      {/* Below lg: one grouped choice, 44 px high, instead of a wrapping row of links. */}
      <div className="lg:hidden">
        <Select
          value={current ?? ""}
          onValueChange={(to) => {
            void navigate(to);
          }}
        >
          <SelectTrigger className="h-11 w-full bg-background" aria-label="Bereich wählen">
            <SelectValue placeholder="Bereich wählen" />
          </SelectTrigger>
          <SelectContent>
            {groups.map((group) =>
              // A group needs its name: without one a screen reader announces an unnamed group.
              group.label === "" ? (
                group.entries.map((entry) => (
                  <SelectItem key={entry.to} value={entry.to}>
                    {entry.label}
                  </SelectItem>
                ))
              ) : (
                <SelectGroup key={group.label}>
                  <SelectLabel className="text-xs tracking-wider uppercase">{group.label}</SelectLabel>
                  {group.entries.map((entry) => (
                    <SelectItem key={entry.to} value={entry.to}>
                      {entry.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ),
            )}
          </SelectContent>
        </Select>
      </div>

      <nav
        aria-label="Hauptnavigation"
        className="hidden w-[220px] shrink-0 lg:sticky lg:top-4 lg:block lg:self-start"
      >
        {groups.map((group) => (
          <div key={group.label} className="mb-5">
            {group.label === "" ? null : (
              <p className="mb-1.5 px-3 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                {group.label}
              </p>
            )}
            <ul>
              {group.entries.map((entry) => {
                const active = current === entry.to;
                const Icon = entry.icon;
                return (
                  <li key={entry.to}>
                    <Link
                      to={entry.to}
                      // "true", not "page", on the pages below an entry: the entry is the
                      // section the current page lies in.
                      aria-current={active ? (pathname === entry.to ? "page" : "true") : undefined}
                      className={cn(
                        "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm",
                        active
                          ? // The bar at the edge carries the mark (5.7:1): the tint alone is 1.12:1.
                            "bg-accent font-semibold text-accent-foreground shadow-[inset_3px_0_0_0_var(--primary)]"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <Icon aria-hidden="true" className="size-4" />
                      {entry.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );
}
